import { useState } from 'react';
import './StrategicPartner.css';
import growattLogo from '../../assets/brand_growatt.png';
import growattCert from '../../assets/growatt_certificate.png';
import growattTeam from '../../assets/growatt_install_team.png';
import growattInstall from '../../assets/brand_growatt_install.png';
import growattDoncaster from '../../assets/rw_doncaster_growatt.png';

const ACCENT = '#1a6fcc';
const ACCENT_SOFT = 'rgba(26, 111, 204, 0.1)';

const slides = [
  { type: 'cert' },
  { type: 'image', src: growattTeam, alt: 'Growatt installation team on site' },
  { type: 'image', src: growattInstall, alt: 'Growatt battery installation' },
  { type: 'image', src: growattDoncaster, alt: 'Growatt battery system, Doncaster VIC' },
];

const features = [
  {
    title: 'Manufacturer-Trained Installation',
    desc: 'Every Watt & Wattle installer working with Growatt systems receives official manufacturer training. This ensures every installation follows Growatt’s latest technical requirements while meeting Australian standards, resulting in safer installations, better system performance, and greater long-term reliability.',
    icon: (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
        <path d="M12 3L1 9l11 6 9-4.91V17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 12.5V17c0 1.1 3.13 3 7 3s7-1.9 7-3v-4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Faster Technical & After-Sales Support',
    desc: 'Because we work directly with Growatt—not through multiple layers of distributors—we have direct access to the manufacturer’s technical team. That means faster responses, quicker troubleshooting, and more efficient warranty and after-sales support when assistance is needed.',
    icon: (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
        <path d="M13 2L4.5 12.5H11l-1 9.5 8.5-11H12l1-9z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Stronger Long-Term Support',
    desc: 'A direct manufacturer relationship gives us ongoing access to product updates, technical resources, firmware improvements, and engineering support throughout the life of your system. As technology evolves, we’re better positioned to continue supporting your installation for years to come.',
    icon: (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l8 3v6c0 5-3.4 8.6-8 11-4.6-2.4-8-6-8-11V5l8-3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Direct Communication with the Manufacturer',
    desc: 'Working directly with Growatt allows us to communicate with the manufacturer whenever technical clarification or engineering advice is required. For more complex situations, this can significantly reduce delays and help resolve issues more efficiently.',
    icon: (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
        <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="16" cy="8" r="3" stroke="currentColor" strokeWidth="1.7" />
        <path d="M2 20v-1c0-2.2 1.8-4 4-4h4c2.2 0 4 1.8 4 4v1M14 15h4c2.2 0 4 1.8 4 4v1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function StrategicPartner() {
  const [current, setCurrent] = useState(0);
  const [openFeature, setOpenFeature] = useState(null);
  const slide = slides[current];

  const go = (dir) => {
    setCurrent((c) => (c + dir + slides.length) % slides.length);
  };

  return (
    <section className="sp-section" style={{ '--accent': ACCENT, '--accent-soft': ACCENT_SOFT }}>
      <div className="sp-inner">

        <div className="sp-top">
          {/* Left — copy (static Growatt) */}
          <div className="sp-left">
            <p className="sp-eyebrow">Official Strategic Partner of</p>
            <img src={growattLogo} alt="Growatt" className="sp-brand-logo" />
            <h2 className="sp-heading">
              Direct Manufacturer.<br />
              Stronger Together.
            </h2>
            <span className="sp-heading-bar" />
            <p className="sp-blurb">
              As an official strategic partner of Growatt, we deliver manufacturer-backed
              expertise, faster support, and long-term confidence for every installation.
            </p>
          </div>

          {/* Right — certificate + installation carousel */}
          <div className="sp-right">
            <div className="sp-stage">
              {slide.type === 'cert' ? (
                <div className="sp-cert-scene">
                  <div className="sp-cert-frame">
                    <img src={growattCert} alt="Growatt Authorized Retailer Certificate — Watt & Wattle Pty Ltd" className="sp-cert-img" />
                  </div>
                  <div className="sp-podium" />
                </div>
              ) : (
                <img src={slide.src} alt={slide.alt} className="sp-stage-img" />
              )}
            </div>

            <button className="sp-arrow sp-arrow-prev" onClick={() => go(-1)} aria-label="Previous">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="#050b14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="sp-arrow sp-arrow-next" onClick={() => go(1)} aria-label="Next">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M9 6l6 6-6 6" stroke="#050b14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div className="sp-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  className={`sp-dot${i === current ? ' active' : ''}`}
                  onClick={() => setCurrent(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom — feature strip (icon + title; full detail on hover / tap) */}
        <div className="sp-features">
          {features.map((f, i) => (
            <div
              key={i}
              className={`sp-feature${openFeature === i ? ' open' : ''}`}
              tabIndex={0}
              role="button"
              aria-expanded={openFeature === i}
              onClick={() => setOpenFeature((prev) => (prev === i ? null : i))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpenFeature((prev) => (prev === i ? null : i));
                }
              }}
            >
              <div className="sp-feature-face">
                <div className="sp-feature-icon">{f.icon}</div>
                <h3 className="sp-feature-title">{f.title}</h3>
                <span className="sp-feature-hint">
                  Read more
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
              <div className="sp-feature-overlay">
                <h3 className="sp-feature-overlay-title">{f.title}</h3>
                <p className="sp-feature-overlay-desc">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
